import { NgModule, ErrorHandler } from '@angular/core'
import { CurrencyPipe, DatePipe } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { HotkeyModule } from 'angular2-hotkeys'
import { NgxEchartsModule } from 'ngx-echarts'

import { MatSidenavModule } from '@angular/material/sidenav'
import { MatSelectModule } from '@angular/material/select'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatOptionModule } from '@angular/material/core'
import { MatPseudoCheckboxModule } from '@angular/material/core'

import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'

import { StoreModule } from '@ngrx/store'
import { EffectsModule } from '@ngrx/effects'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store'

import { RetryInterceptor } from '@shared/interceptors/retry.interceptor'
import { SharedModule } from '@shared/shared.module'
import { AuthInterceptor } from '@shared/interceptors/auth.interceptor'
import { ShortNumberPipe } from '@shared/pipes/short-number.pipe'
import { LoadingPopupComponent } from '@shared/loading-popup/loading-popup.component'

import { metaReducers, reducers } from './core/store'
import { AppEffects } from './core/store/app.effects'
import { AuthEffects } from './core/store/auth/auth.effects'
import { ProgramEffects } from './core/store/program/program.effects'

import { ErrorLoggingService } from './error-logging.service'
import { ErrorModule } from './error/error.module'
import { environment } from '../environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { AuthCallBackComponent } from './core/auth/auth-callback.component'
import { DebugContainerComponent } from './core/debug.container'
import { CustomSerializer } from './core/store/custom-serializer'
import { RoleGuard } from './guards/role-guard.service'
import { NavListContainerComponent } from './nav-list.container'
import { NavService } from './nav.service'
import { BnNgIdleService } from 'bn-ng-idle'
import { TimeoutDialogComponent } from './timeout-dialog.component'
import { ContactSupportModule } from './contact-support/contact-support.module'
import { MaintenanceModule } from './maintenance/maintenance.module'
import { LandingPageComponent } from './landing-page/landing-page.component'
import { HomePageComponent } from './home/home-page.component'

const CONDITIONAL_IMPORTS = []
if (!environment.production) {
  CONDITIONAL_IMPORTS.push(
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 50, // Retains last 50 states
      logOnly: environment.production, // Restrict extension to log-only mode
      trace: true,
      connectInZone: true,
    }),
  )
} else {
  CONDITIONAL_IMPORTS.push(
    StoreModule.forRoot(reducers, {
      metaReducers,
    }),
  )
}

@NgModule({
  declarations: [
    TimeoutDialogComponent,
    AppComponent,
    AuthCallBackComponent,
    DebugContainerComponent,
    NavListContainerComponent,
    LoadingPopupComponent,
    LandingPageComponent,
    HomePageComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    ContactSupportModule,
    AppRoutingModule,
    StoreModule.forRoot(reducers, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    ...CONDITIONAL_IMPORTS,
    StoreRouterConnectingModule.forRoot({ serializer: CustomSerializer }),
    EffectsModule.forRoot([AppEffects, AuthEffects, ProgramEffects]),
    ErrorModule,
    MatSidenavModule,
    HotkeyModule.forRoot(),
    MatPseudoCheckboxModule,
    MatSelectModule,
    MatCheckboxModule,
    MatOptionModule,
    MaintenanceModule,
    NgxEchartsModule.forRoot({ echarts: () => import('echarts') }),
  ],
  providers: [
    CurrencyPipe,
    ShortNumberPipe,
    DatePipe,
    RoleGuard,
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    { provide: HTTP_INTERCEPTORS, useClass: RetryInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorLoggingService },
    NavService,
    BnNgIdleService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
